// src/views/admin/AlertSystemPage.js
// ═══════════════════════════════════════════════════════════════════════════
// FlowSense MOW+ · Alert System
// --- UPDATE (Alert System AI · v3) -------------------------------------------
// The page now only lays out components; data, live updates and actions live in
// hooks/useAlertSystem.js. Everything the old page did is still here:
//   active alerts (filters, paging, details, acknowledge / resolve, bulk, export, sound),
//   simple rules (CRUD, enable / disable), compound rules, AI rule suggestions, live WebSocket.
// New:
//   • FlowSense AI alert monitor panel (health, insights, incidents, priority queue, Ask AI)
//   • KPI strip with MTTA / MTTR / today vs usual day (server-side numbers)
//   • AI triage score column, AI-highlighted rows, AI rule health on the rules tab
//   • Trends & incidents tab (14-day chart, incidents, rules to tune, silent sensors)
//   • Bulk acknowledge / resolve that works (selection is owned by the page now), with notes
//   • Alert modal: FlowSense AI explanation, readings chart, act without leaving the modal
// Fixed: "Create rule" from an AI suggestion (was treated as an edit of rule undefined),
//   stats crawl of up to 100 requests per refresh, beeping on every refresh, double fetch on
//   every keystroke, bulk buttons that never appeared.
// --- END UPDATE --------------------------------------------------------------
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Badge, Box, Button, Flex, Grid, GridItem, Heading, HStack, Icon, SimpleGrid, Skeleton, Stack, Tab, TabList,
    TabPanel, TabPanels, Tabs, Text, Tooltip, useBreakpointValue, useColorModeValue, useDisclosure, useToast, VStack,
} from '@chakra-ui/react';
import {
    MdAdd, MdNotifications, MdSettings, MdSpeed, MdAutoAwesome, MdInsights, MdRefresh, MdSensorsOff, MdHub,
} from 'react-icons/md';

import AlertRuleForm from '../../components/alerts/AlertRuleForm';
import AlertRulesTable from '../../components/alerts/AlertRulesTable';
import ActiveAlertsTable from '../../components/alerts/ActiveAlertsTable';
import ViewActiveAlertModal from '../../components/alerts/ViewActiveAlertModal';
import CompoundAlertRuleForm from '../../components/alerts/CompoundAlertRuleForm';
import CompoundAlertRulesTable from '../../components/alerts/CompoundAlertRulesTable';
import AiSuggestedRules from '../../components/alerts/AiSuggestedRules';
import * as compoundAlertRuleService from '../../services/compoundAlertRuleService';
// --- UPDATE (Alert System AI): new components
import AlertCommandHeader from '../../components/alerts/AlertCommandHeader';
import AlertKpiStrip from '../../components/alerts/AlertKpiStrip';
import FlowSenseAlertAiPanel from '../../components/alerts/FlowSenseAlertAiPanel';
import AlertFiltersBar from '../../components/alerts/AlertFiltersBar';
import AlertPagination from '../../components/alerts/AlertPagination';
import BulkAlertActionBar from '../../components/alerts/BulkAlertActionBar';
import AlertTrendChart from '../../components/alerts/AlertTrendChart';
import AlertPerformanceDrawer from '../../components/alerts/AlertPerformanceDrawer';
import { ALERT_STATUS_OPTIONS, SEVERITY_OPTIONS, severityColor } from '../../components/alerts/alertUtils';
import useAlertSystem from '../../hooks/useAlertSystem';
// --- END UPDATE
import { useNotifications } from '../../contexts/NotificationContext';

const TAB = { ALERTS: 0, RULES: 1, COMPOUND: 2, SUGGESTIONS: 3, TRENDS: 4 };
const IS_DEV = process.env.NODE_ENV === 'development';

const Card = ({ children, ...rest }) => {
    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.100', 'gray.700');
    return <Box bg={bg} p={{ base: 4, md: 5 }} borderRadius="2xl" borderWidth="1px" borderColor={border} boxShadow="sm" {...rest}>{children}</Box>;
};

function AlertSystemPage() {
    const toast = useToast();
    const sys = useAlertSystem({ toast });
    const { isConnected } = useNotifications();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const muted = useColorModeValue('gray.500', 'gray.400');
    const renders = useRef(0);
    renders.current += 1;

    const [tabIndex, setTabIndex] = useState(TAB.ALERTS);
    const [selectedRule, setSelectedRule] = useState(null);
    const [selectedCompoundRule, setSelectedCompoundRule] = useState(null);
    const [refreshCompoundRules, setRefreshCompoundRules] = useState(false);
    const [viewAlert, setViewAlert] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const ruleForm = useDisclosure();
    const compoundForm = useDisclosure();
    const alertModal = useDisclosure();
    const perfDrawer = useDisclosure();

    const triageById = sys.intel?.triage_by_id;
    const ruleHealth = sys.intel?.rule_health;

    // ── alerts ───────────────────────────────────────────────────────────────
    const openAlert = useCallback(async (alertOrId) => {
        const a = typeof alertOrId === 'object' ? alertOrId : await sys.openAlertById(alertOrId);
        if (!a) return;
        setViewAlert(a);
        alertModal.onOpen();
    }, [sys, alertModal]);

    const onAcknowledgeOne = useCallback((id) => sys.acknowledge([id]), [sys]);
    const onResolveOne = useCallback((id) => {
        if (!window.confirm('Mark this alert as resolved?')) return;
        sys.resolve([id]);
    }, [sys]);
    const onBulkResolve = useCallback(async (note) => {
        if (!window.confirm(`Resolve ${sys.selectedIds.size} alert(s)?`)) return false;
        return sys.resolve([...sys.selectedIds], note);
    }, [sys]);

    const focusAlerts = useCallback((ids) => {
        sys.highlight(ids);
        setTabIndex(TAB.ALERTS);
        const onPage = sys.alerts.filter(a => ids.includes(a.id)).length;
        toast({
            title: `${ids.length} alert${ids.length > 1 ? 's' : ''} highlighted`,
            description: onPage < ids.length ? `${onPage} on this page — clear filters or change page to see the rest.` : 'Highlighted in purple below.',
            status: 'info', duration: 3500,
        });
    }, [sys, toast]);

    const filterStatus = useCallback((status) => {
        sys.setAlertFilter('status', status);
        setTabIndex(TAB.ALERTS);
    }, [sys]);

    // ── rules ────────────────────────────────────────────────────────────────
    const openRuleForm = useCallback((rule) => { setSelectedRule(rule || null); ruleForm.onOpen(); }, [ruleForm]);
    const onRuleSubmit = useCallback(async (data) => {
        const ok = await sys.saveRule(selectedRule?.id || null, data); // --- UPDATE: a suggestion has no id → create
        if (ok) ruleForm.onClose();
    }, [sys, selectedRule, ruleForm]);
    const onDeleteRule = useCallback((id) => {
        if (window.confirm('Delete this alert rule? Its open alerts stay, but it will stop checking.')) sys.deleteRule(id);
    }, [sys]);
    const openRuleById = useCallback((id) => {
        const r = sys.rules.find(x => x.id === id);
        setTabIndex(TAB.RULES);
        if (r) openRuleForm(r);
        else sys.setRuleFilter('search', ruleHealth?.[id]?.name || '');
    }, [sys, openRuleForm, ruleHealth]);

    const handleCreateFromSuggestion = useCallback((p) => {
        openRuleForm({
            name: p.name, sensor_id: p.sensor_id, asset_id: p.asset_id, metric_type: p.metric_type,
            condition_type: p.condition_type, threshold_value: p.threshold_value, severity: p.severity, is_enabled: true,
        });
    }, [openRuleForm]);

    const handleEditCompoundRule = useCallback(async (ruleId) => {
        try {
            setSelectedCompoundRule(await compoundAlertRuleService.fetchCompoundAlertRuleById(ruleId));
            compoundForm.onOpen();
        } catch (e) {
            toast({ title: 'Error fetching compound rule', description: e.message, status: 'error' });
        }
    }, [compoundForm, toast]);

    const refreshEverything = useCallback(async () => {
        setRefreshing(true);
        try { await sys.refreshAll({ refreshAi: true }); } finally { setRefreshing(false); }
    }, [sys]);

    const tabs = useMemo(() => ([
        { icon: MdNotifications, label: 'Alerts', badge: sys.stats?.alerts?.open, color: 'red' },
        { icon: MdSettings, label: 'Rules', badge: sys.stats?.rules?.total, color: 'blue' },
        { icon: MdSpeed, label: 'Compound rules', badge: null, color: 'teal' },
        { icon: MdAutoAwesome, label: 'AI suggestions', badge: sys.suggestions.length, color: 'purple' },
        { icon: MdInsights, label: 'Trends & incidents', badge: sys.intel?.incidents?.length, color: 'orange' },
    ]), [sys.stats, sys.suggestions.length, sys.intel]);

    const perfMetrics = useMemo(() => ({
        renders: renders.current,
        alerts_on_page: sys.alerts.length,
        ai_compute_ms: sys.intel?.compute_ms,
        ai_cached: sys.intel?.cached ? 'yes' : 'no',
    }), [sys.alerts.length, sys.intel]);

    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
            <AlertCommandHeader
                isConnected={!!isConnected}
                health={sys.intel?.health}
                lastUpdated={sys.lastUpdated}
                soundEnabled={sys.soundEnabled}
                onToggleSound={sys.setSoundEnabled}
                onExport={sys.exportAlerts}
                onRefresh={refreshEverything}
                isRefreshing={refreshing}
                onOpenPerf={IS_DEV ? perfDrawer.onOpen : undefined}
            />

            <AlertKpiStrip stats={sys.stats} intel={sys.intel} loading={sys.intelLoading} />

            <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 380px' }} gap={6} alignItems="start">
                <GridItem minW={0}>
                    <Tabs index={tabIndex} onChange={setTabIndex} variant="soft-rounded" colorScheme="blue" isLazy lazyBehavior="keepMounted">
                        <TabList mb={4} flexWrap="wrap" gap={2}>
                            {tabs.map(t => (
                                <Tab key={t.label} fontWeight="600" borderRadius="xl" _selected={{ bg: `${t.color}.500`, color: 'white' }} aria-label={t.label}>
                                    <HStack spacing={2}>
                                        <Icon as={t.icon} />
                                        {!isMobile && <Text>{t.label}</Text>}
                                        {t.badge > 0 && <Badge colorScheme={t.color} borderRadius="full" fontSize="xs">{t.badge}</Badge>}
                                    </HStack>
                                </Tab>
                            ))}
                        </TabList>

                        <TabPanels>
                            {/* ── ALERTS ── */}
                            <TabPanel px={0}>
                                <Card>
                                    <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
                                        <Heading size="md">Sensor alerts</Heading>
                                        {sys.highlightIds.size > 0 && (
                                            <Button size="xs" variant="ghost" colorScheme="purple" onClick={() => sys.highlight([])}>Clear highlight ({sys.highlightIds.size})</Button>
                                        )}
                                    </Flex>
                                    <AlertFiltersBar
                                        search={sys.alertFilters.search}
                                        onSearch={v => sys.setAlertFilter('search', v)}
                                        isSearching={sys.isSearchingAlerts}
                                        placeholder="Search message, sensor, asset or rule…"
                                        selects={[
                                            { name: 'status', placeholder: 'Status', options: ALERT_STATUS_OPTIONS, maxW: '200px' },
                                            { name: 'severity', placeholder: 'Severity', options: SEVERITY_OPTIONS },
                                        ]}
                                        values={sys.alertFilters}
                                        onSelectChange={sys.setAlertFilter}
                                        onClear={sys.resetAlertFilters}
                                    />
                                    <BulkAlertActionBar
                                        count={sys.selectedIds.size}
                                        onAcknowledge={note => sys.acknowledge([...sys.selectedIds], note)}
                                        onResolve={onBulkResolve}
                                        onClear={sys.clearSelection}
                                        isWorking={sys.isWorking}
                                    />
                                    {sys.alertsError && !sys.alerts.length ? (
                                        <VStack py={10} spacing={3}>
                                            <Text color="orange.400">{sys.alertsError}</Text>
                                            <Button size="sm" leftIcon={<MdRefresh />} onClick={() => sys.fetchAlerts()}>Try again</Button>
                                        </VStack>
                                    ) : sys.alertsLoading && !sys.alerts.length ? (
                                        <Stack spacing={3}>{[0, 1, 2, 3].map(i => <Skeleton key={i} h="56px" borderRadius="lg" />)}</Stack>
                                    ) : (
                                        <ActiveAlertsTable
                                            alerts={sys.alerts}
                                            onAcknowledge={onAcknowledgeOne}
                                            onResolve={onResolveOne}
                                            onViewDetails={openAlert}
                                            isLoading={sys.alertsLoading}
                                            selectedIds={sys.selectedIds}
                                            onToggleSelect={sys.toggleSelect}
                                            onToggleSelectAll={sys.toggleSelectAll}
                                            triageById={triageById}
                                            highlightIds={sys.highlightIds}
                                        />
                                    )}
                                    <AlertPagination
                                        pagination={sys.alertPagination}
                                        onPageChange={p => sys.setAlertFilter('page', p)}
                                        onLimitChange={n => sys.setAlertFilter('limit', n)}
                                        isLoading={sys.alertsLoading}
                                    />
                                </Card>
                            </TabPanel>

                            {/* ── RULES ── */}
                            <TabPanel px={0}>
                                <Card>
                                    <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
                                        <Box>
                                            <Heading size="md">Simple alert rules</Heading>
                                            {ruleHealth && Object.keys(ruleHealth).length > 0 && (
                                                <Text fontSize="sm" color={muted}>FlowSense AI flagged {Object.keys(ruleHealth).length} rule(s) as noisy or flapping — see the badges.</Text>
                                            )}
                                        </Box>
                                        <Button leftIcon={<MdAdd />} colorScheme="teal" borderRadius="xl" onClick={() => openRuleForm(null)}>New rule</Button>
                                    </Flex>
                                    <AlertFiltersBar
                                        search={sys.ruleFilters.search}
                                        onSearch={v => sys.setRuleFilter('search', v)}
                                        isSearching={sys.isSearchingRules}
                                        placeholder="Search rules, sensors, locations…"
                                        selects={[
                                            { name: 'is_enabled', placeholder: 'State', options: [{ value: 'true', label: 'Enabled' }, { value: 'false', label: 'Disabled' }] },
                                            { name: 'severity', placeholder: 'Severity', options: SEVERITY_OPTIONS },
                                        ]}
                                        values={sys.ruleFilters}
                                        onSelectChange={sys.setRuleFilter}
                                        onClear={() => { sys.setRuleFilter('search', ''); sys.setRuleFilter('is_enabled', ''); sys.setRuleFilter('severity', ''); }}
                                    />
                                    {sys.rulesLoading && !sys.rules.length ? (
                                        <Stack spacing={3}>{[0, 1, 2].map(i => <Skeleton key={i} h="56px" borderRadius="lg" />)}</Stack>
                                    ) : (
                                        <AlertRulesTable
                                            rules={sys.rules}
                                            onEdit={openRuleForm}
                                            onDelete={onDeleteRule}
                                            onToggleEnable={sys.toggleRule}
                                            isLoading={sys.rulesLoading}
                                            ruleHealth={ruleHealth}
                                            onApplyTuning={sys.applyRuleTuning}
                                        />
                                    )}
                                    <AlertPagination
                                        pagination={sys.rulePagination}
                                        onPageChange={p => sys.setRuleFilter('page', p)}
                                        onLimitChange={n => sys.setRuleFilter('limit', n)}
                                        isLoading={sys.rulesLoading}
                                    />
                                </Card>
                            </TabPanel>

                            {/* ── COMPOUND ── */}
                            <TabPanel px={0}>
                                <Card>
                                    <Flex justify="space-between" align="center" mb={2} gap={3} flexWrap="wrap">
                                        <Box>
                                            <Heading size="md">Compound alert rules</Heading>
                                            <Text fontSize="sm" color={muted}>Raise one alert when several simple rules fire in sequence within their time windows (e.g. pressure drop, then low level downstream).</Text>
                                        </Box>
                                        <Button leftIcon={<MdAdd />} colorScheme="purple" borderRadius="xl" onClick={() => { setSelectedCompoundRule(null); compoundForm.onOpen(); }}>New compound rule</Button>
                                    </Flex>
                                    <CompoundAlertRulesTable onEdit={handleEditCompoundRule} onRefresh={refreshCompoundRules} />
                                </Card>
                            </TabPanel>

                            {/* ── AI SUGGESTIONS ── */}
                            <TabPanel px={0}>
                                <Card>
                                    <Flex justify="flex-end" mb={2}>
                                        <Button size="sm" variant="ghost" colorScheme="purple" leftIcon={<MdRefresh />} onClick={sys.fetchSuggestions} isLoading={sys.suggestionsLoading}>Refresh</Button>
                                    </Flex>
                                    <AiSuggestedRules suggestions={sys.suggestions} isLoading={sys.suggestionsLoading} onCreateRule={handleCreateFromSuggestion} />
                                </Card>
                            </TabPanel>

                            {/* ── TRENDS & INCIDENTS ── */}
                            <TabPanel px={0}>
                                <Stack spacing={5}>
                                    <Card>
                                        <Heading size="md" mb={3}>Alerts per day · last 14 days</Heading>
                                        {sys.intel ? <AlertTrendChart trend={sys.intel.trend} storm={sys.intel.storm} /> : <Skeleton h="180px" />}
                                    </Card>
                                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
                                        <Card>
                                            <HStack mb={3}><Icon as={MdHub} color="orange.400" /><Heading size="sm">Incidents (related open alerts)</Heading></HStack>
                                            {!sys.intel ? <Skeleton h="80px" /> : sys.intel.incidents?.length ? (
                                                <VStack align="stretch" spacing={2}>
                                                    {sys.intel.incidents.map(inc => (
                                                        <Flex key={inc.id} p={3} borderWidth="1px" borderRadius="lg" justify="space-between" align="center" gap={3}>
                                                            <Box minW={0}>
                                                                <HStack><Badge colorScheme={severityColor(inc.severity)}>{inc.severity}</Badge><Text fontWeight="700" fontSize="sm" noOfLines={1}>{inc.title}</Text></HStack>
                                                                <Text fontSize="xs" color={muted} noOfLines={1}>{inc.sensors.join(', ')}</Text>
                                                            </Box>
                                                            <Button size="xs" onClick={() => focusAlerts(inc.alert_ids)}>Show</Button>
                                                        </Flex>
                                                    ))}
                                                </VStack>
                                            ) : <Text fontSize="sm" color={muted}>No related open alerts — nothing points to a shared cause.</Text>}
                                        </Card>
                                        <Card>
                                            <HStack mb={3}><Icon as={MdSensorsOff} color="gray.400" /><Heading size="sm">Monitored sensors with no data (24 h)</Heading></HStack>
                                            {!sys.intel ? <Skeleton h="80px" /> : sys.intel.silent_sensors?.length ? (
                                                <VStack align="stretch" spacing={1}>
                                                    {sys.intel.silent_sensors.map(s => (
                                                        <Tooltip key={s.sensor_id} hasArrow label="Has an enabled rule but sent no reading in 24 h — its rules cannot fire.">
                                                            <Text fontSize="sm">• {s.sensor_name || s.sensor_id} <Text as="span" color={muted}>({s.sensor_id})</Text></Text>
                                                        </Tooltip>
                                                    ))}
                                                </VStack>
                                            ) : <Text fontSize="sm" color={muted}>Every sensor with an enabled rule reported in the last 24 h.</Text>}
                                        </Card>
                                    </SimpleGrid>
                                </Stack>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </GridItem>

                <GridItem position={{ xl: 'sticky' }} top={{ xl: '90px' }}>
                    <FlowSenseAlertAiPanel
                        intel={sys.intel}
                        loading={sys.intelLoading}
                        error={sys.intelError}
                        onRefresh={() => sys.fetchIntel({ refresh: true })}
                        onFocusAlerts={focusAlerts}
                        onFilter={filterStatus}
                        onOpenAlert={openAlert}
                        onApplyRuleTuning={sys.applyRuleTuning}
                        onOpenRule={openRuleById}
                        isConnected={!!isConnected}
                    />
                </GridItem>
            </Grid>

            {ruleForm.isOpen && (
                <AlertRuleForm isOpen={ruleForm.isOpen} onClose={ruleForm.onClose} onSubmit={onRuleSubmit} initialData={selectedRule} isLoading={sys.isWorking} />
            )}
            {compoundForm.isOpen && (
                <CompoundAlertRuleForm isOpen={compoundForm.isOpen} onClose={compoundForm.onClose} onSave={() => setRefreshCompoundRules(v => !v)} initialData={selectedCompoundRule} />
            )}
            {alertModal.isOpen && viewAlert && (
                <ViewActiveAlertModal
                    isOpen={alertModal.isOpen}
                    onClose={alertModal.onClose}
                    alert={viewAlert}
                    triage={triageById?.[viewAlert.id]}
                    onAcknowledge={sys.acknowledge}
                    onResolve={sys.resolve}
                />
            )}
            {IS_DEV && <AlertPerformanceDrawer isOpen={perfDrawer.isOpen} onClose={perfDrawer.onClose} metrics={perfMetrics} />}
        </Box>
    );
}

export default AlertSystemPage;
